import React, { useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import axios from '../api';
import ChatPanel from '../chat/ChatPanel';
import AnnouncementPanel from '../components/AnnouncementPanel';
import HomeStats from '../components/HomeStats';
import RecentActivity from '../components/RecentActivity';
import AgentList from '../features/agent/AgentList';
import CloudDriveSync from '../features/cloud/CloudDriveSync';
import SpaceManage from '../features/cloud/SpaceManage';
import SpaceMemberManage from '../features/cloud/SpaceMemberManage';
import DocumentManage from '../features/document/DocumentManage';
import KnowledgeManage from '../features/knowledge/KnowledgeManage';
import AnnouncementManage from '../features/setting/AnnouncementManage';
import ApiManage from '../features/setting/ApiManage';
import TokenStats from '../features/setting/TokenStats';
import UserProfile from '../features/setting/UserProfile';
import GroupList from '../features/user/GroupList';
import MemberList from '../features/user/MemberList';
import UserGroupMemberManage from '../features/user/UserGroupMemberManage';
import ScheduleCalendarPage from '../features/schedule/ScheduleCalendarPage';
import SchedulePersonDetail from '../features/schedule/SchedulePersonDetail';
import ScheduleSettings from '../features/schedule/ScheduleSettings';
import theme from '../theme';
import permissionManager from '../utils/permissions';

const menuList = [
  { key: 'home', label: '首页' },
  { key: 'schedule', label: '排课系统' },
  {
    key: 'document', label: '文档中心', children: [
      { key: 'document-manage', label: '文档管理' },
      { key: 'space-manage', label: '空间管理' },
    ]
  },
  {
    key: 'knowledge', label: '知识库', children: [
      { key: 'knowledge-mgr', label: '知识库管理' },
    ]
  },
  {
    key: 'agent', label: '智能体', children: [
      { key: 'agent-mgr', label: '智能体管理' },
    ]
  },
  {
    key: 'user', label: '用户管理', children: [
      { key: 'member-list', label: '会员列表' },
      { key: 'group-list', label: '用户组管理' },
    ]
  },
  {
    key: 'setting', label: '设置', children: [
      { key: 'token', label: 'token统计' },
      { key: 'api', label: 'API接口管理' },
      { key: 'announcement', label: '公告管理' },
    ]
  },
];

const SidebarMenuItem = ({ item, isOpen, selectedKey, setSelectedKey, onChildClick }) => {
  const submenuRef = useRef(null);
  const [submenuHeight, setSubmenuHeight] = useState(0);
  useEffect(() => {
    if (isOpen && submenuRef.current) {
      setSubmenuHeight(submenuRef.current.scrollHeight);
    } else {
      setSubmenuHeight(0);
    }
  }, [isOpen, item.children?.length]);
  return (
    <div style={{ marginBottom: isOpen ? 0 : 8 }}>
      <div
        style={{
          fontWeight: 700,
          fontSize: 19,
          padding: '14px 32px 6px 24px',
          borderRadius: '0 20px 20px 0',
          background: 'none',
          color: '#231a17',
          cursor: 'pointer',
          transition: 'all 0.2s',
          boxShadow: 'none',
          letterSpacing: 0.5,
          marginBottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span>{item.label}</span>
        {item.children && (
          <span
            style={{
              marginLeft: 8,
              display: 'inline-block',
              transition: 'transform 0.2s',
              transform: isOpen ? 'rotate(180deg)' : 'none',
              verticalAlign: 'middle',
            }}
          >
            <svg width="22" height="16" viewBox="0 0 22 16" style={{ display: 'block' }}>
              <polyline points="4,6 11,13 18,6" fill="none" stroke="#888" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        )}
      </div>
      {item.children && (
        <div
          ref={submenuRef}
          style={{
            marginLeft: 24,
            marginTop: 0,
            height: submenuHeight,
            overflow: 'hidden',
            transition: 'height 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {item.children.map(child => (
              <div
                key={child.key}
                onClick={e => { 
                  e.stopPropagation(); 
                  setSelectedKey(child.key);
                  if (onChildClick) {
                    onChildClick(child);
                  }
                }}
                style={{
                  padding: '6px 0 6px 18px',
                  borderRadius: 0,
                  margin: '0 0 0 0',
                  background: selectedKey === child.key ? '#231a17' : 'none',
                  color: selectedKey === child.key ? theme.PRIMARY_COLOR : '#231a17',
                  fontWeight: selectedKey === child.key ? 700 : 400,
                  fontSize: 15,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  letterSpacing: 0.2,
                }}
              >
                {child.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const HEADER_HEIGHT = 64; // 顶部栏高度，调整为64px

const MENU_KEY_TO_PATH = {
  home: '/home',
  schedule: '/schedules',
  document: '/documents',
  'document-manage': '/documents',
  'space-manage': '/spaces',
  'cloud-drive-sync': '/cloud-sync',
  'knowledge-mgr': '/knowledge',
  'agent-mgr': '/agents',
  'member-list': '/users',
  'group-list': '/users/groups',
  chat: '/chat',
  token: '/settings/token',
  api: '/settings/api',
  announcement: '/settings/announcements',
  profile: '/profile',
};

const pathForMenuKey = (key) => MENU_KEY_TO_PATH[key] || '/home';

const syncMenuStateFromPath = (pathname) => {
  if (pathname === '/home' || pathname === '/dashboard') {
    return { selectedKey: 'home', openMenu: null };
  }
  if (pathname.startsWith('/schedules')) {
    return { selectedKey: 'schedule', openMenu: null };
  }
  if (pathname.startsWith('/documents')) {
    return { selectedKey: 'document-manage', openMenu: 'document' };
  }
  if (pathname.startsWith('/spaces')) {
    return { selectedKey: 'space-manage', openMenu: 'document' };
  }
  if (pathname.startsWith('/cloud-sync')) {
    return { selectedKey: 'cloud-drive-sync', openMenu: 'document' };
  }
  if (pathname.startsWith('/knowledge')) {
    return { selectedKey: 'knowledge-mgr', openMenu: 'knowledge' };
  }
  if (pathname.startsWith('/agents')) {
    return { selectedKey: 'agent-mgr', openMenu: 'agent' };
  }
  if (pathname.startsWith('/users/groups')) {
    return { selectedKey: 'group-list', openMenu: 'user' };
  }
  if (pathname.startsWith('/users')) {
    return { selectedKey: 'member-list', openMenu: 'user' };
  }
  if (pathname.startsWith('/chat')) {
    return { selectedKey: 'chat', openMenu: 'setting' };
  }
  if (pathname.startsWith('/settings/token')) {
    return { selectedKey: 'token', openMenu: 'setting' };
  }
  if (pathname.startsWith('/settings/api')) {
    return { selectedKey: 'api', openMenu: 'setting' };
  }
  if (pathname.startsWith('/settings/announcements')) {
    return { selectedKey: 'announcement', openMenu: 'setting' };
  }
  if (pathname.startsWith('/profile')) {
    return { selectedKey: 'profile', openMenu: 'setting' };
  }
  return null;
};

const PermissionDenied = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '400px',
    flexDirection: 'column',
    gap: 16
  }}>
    <div style={{ fontSize: 48 }}>🚫</div>
    <div style={{ fontSize: 24, color: '#666' }}>权限不足</div>
    <div style={{ color: '#999' }}>您没有访问此页面的权限</div>
  </div>
);

const DashboardHome = () => (
  <div style={{
    padding: '32px',
    background: '#fff',
    minHeight: '100%',
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'auto',
    flex: 1,
  }}>
    <div style={{ marginBottom: 32, width: '100%' }}>
      <HomeStats />
    </div>
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: 32,
      marginBottom: 32,
      width: '100%',
    }}>
      <div style={{ minWidth: 0 }}>
        <AnnouncementPanel />
      </div>
      <div style={{ minWidth: 0 }}>
        <RecentActivity />
      </div>
    </div>
  </div>
);

// 取侧边栏第一个菜单 key（用于登录后默认页）
const getFirstVisibleMenuKey = (list) => {
  if (!list || list.length === 0) return 'home';
  const first = list[0];
  return first.children && first.children.length > 0 ? first.children[0].key : first.key;
};

const Dashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedKey, setSelectedKey] = useState(() => getFirstVisibleMenuKey(menuList));
  const [openMenus, setOpenMenus] = useState([]);
  const [userInfo, setUserInfo] = useState({ username: '', real_name: '' });
  const [filteredMenuList, setFilteredMenuList] = useState(menuList);
  const [showProfile, setShowProfile] = useState(false);

  const applyMenuState = (selectedKey, openMenu) => {
    setSelectedKey(selectedKey);
    if (openMenu) {
      setOpenMenus(prev => (prev.includes(openMenu) ? prev : [...prev, openMenu]));
    }
  };

  // 根据 URL 同步侧边栏选中项
  useEffect(() => {
    const synced = syncMenuStateFromPath(location.pathname);
    if (synced) {
      applyMenuState(synced.selectedKey, synced.openMenu);
    }
  }, [location.pathname]);

  // 登录后访问 /dashboard 时，跳转到侧边栏第一个菜单页
  useEffect(() => {
    if (location.pathname === '/dashboard') {
      const firstKey = getFirstVisibleMenuKey(filteredMenuList);
      navigate(pathForMenuKey(firstKey), { replace: true });
    }
  }, [location.pathname, filteredMenuList, navigate]);

  useEffect(() => {
    // 获取用户信息和权限
    const fetchUserData = async () => {
      try {
        const [userInfoRes] = await Promise.all([
          axios.get('/api/userinfo/')
        ]);

        setUserInfo(userInfoRes.data);

        // 加载权限管理器
        await permissionManager.loadPermissions();
        
        // 根据权限过滤菜单 - 使用permissionManager
        const filtered = menuList.filter(item => {
          if (item.key === 'home') return true;
          
          // 检查模块权限
          const moduleKey = item.key;
          return permissionManager.hasModulePermission(moduleKey);
        });
        
        setFilteredMenuList(filtered);

        const synced = syncMenuStateFromPath(location.pathname);
        if (synced) {
          applyMenuState(synced.selectedKey, synced.openMenu);
        } else if (filtered.length > 0) {
          const firstKey = getFirstVisibleMenuKey(filtered);
          applyMenuState(firstKey, filtered[0].children?.length ? filtered[0].key : null);
        }
      } catch (error) {
        console.error('获取用户数据失败:', error);
        setUserInfo({ username: localStorage.getItem('username') || '' });
        setFilteredMenuList(menuList); // 如果获取权限失败，显示所有菜单
      }
    };

    fetchUserData();
  }, [location.pathname]);

  // 辅助函数：获取一级菜单的第一个子菜单key
  const getFirstChildKey = (item) => {
    if (item.children && item.children.length > 0) {
      return item.children[0].key;
    }
    return item.key;
  };

  // 优化点击一级菜单时自动选中第一个子菜单
  const handleMenuClick = (item) => {
    if (item.children && item.children.length > 0) {
      // 切换展开/收起
      setOpenMenus(prev => prev.includes(item.key)
        ? prev.filter(k => k !== item.key)
        : [...prev, item.key]);
    } else {
      setSelectedKey(item.key);
      navigate(pathForMenuKey(item.key));
    }
  };

  // 处理子菜单项点击：导航到对应路径，避免被“/ 跳转排课”逻辑覆盖
  const handleChildMenuClick = (child) => {
    setSelectedKey(child.key);
    navigate(pathForMenuKey(child.key));
  };

  // 退出登录
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user_info');
    localStorage.removeItem('username');
    window.location.replace('/login');
  };

  // 渲染侧边栏菜单
  const renderMenu = () => (
    <div style={{
      width: 180,
      background: theme.PRIMARY_COLOR,
      minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
      boxShadow: '2px 0 8px #e5e5e5',
      borderRadius: 0,
      padding: '32px 0',
      display: 'flex',
      flexDirection: 'column',
      gap: 0,
      color: '#231a17',
      fontFamily: 'inherit',
    }}>
      {filteredMenuList.map(item => {
        const isOpen = openMenus.includes(item.key);
        return (
          <div key={item.key} onClick={() => handleMenuClick(item)}>
            <SidebarMenuItem
              item={item}
              isOpen={isOpen}
              selectedKey={selectedKey}
              setSelectedKey={setSelectedKey}
              onChildClick={handleChildMenuClick}
            />
          </div>
        );
      })}
    </div>
  );

  // 渲染内容区
  const renderContent = () => {
    const checkPagePermission = (module) => {
      return permissionManager.hasModulePermission(module);
    };

    return (
      <Routes>
        {/* 用户组成员管理路由 */}
        <Route
          path="/user-group/:groupId/members"
          element={<UserGroupMemberManage />}
        />
        {/* 排课系统路由 */}
        <Route
          path="/schedules"
          element={
            checkPagePermission('schedule') ? (
              <div style={{ flex: 1, minHeight: 0, height: '100%', minHeight: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <ScheduleCalendarPage />
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', flexDirection: 'column', gap: 16 }}>
                <div style={{ fontSize: 48 }}>🚫</div>
                <div style={{ fontSize: 24, color: '#666' }}>权限不足</div>
              </div>
            )
          }
        />
        <Route
          path="/schedules/person/:id"
          element={
            checkPagePermission('schedule') ? (
              <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <SchedulePersonDetail />
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', flexDirection: 'column', gap: 16 }}>
                <div style={{ fontSize: 48 }}>🚫</div>
                <div style={{ fontSize: 24, color: '#666' }}>权限不足</div>
              </div>
            )
          }
        />
        <Route
          path="/schedules/settings"
          element={
            checkPagePermission('schedule') ? (
              <ScheduleSettings />
            ) : (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', flexDirection: 'column', gap: 16 }}>
                <div style={{ fontSize: 48 }}>🚫</div>
                <div style={{ fontSize: 24, color: '#666' }}>权限不足</div>
              </div>
            )
          }
        />
        {/* 文件管理 */}
        <Route
          path="/documents"
          element={
            checkPagePermission('document') ? (
              <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <DocumentManage />
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', flexDirection: 'column', gap: 16 }}>
                <div style={{ fontSize: 48 }}>🚫</div>
                <div style={{ fontSize: 24, color: '#666' }}>权限不足</div>
              </div>
            )
          }
        />
        <Route
          path="/spaces"
          element={
            checkPagePermission('document') ? (
              <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <SpaceManage />
              </div>
            ) : (
              <PermissionDenied />
            )
          }
        />
        <Route
          path="/cloud-sync"
          element={
            checkPagePermission('document') ? (
              <CloudDriveSync />
            ) : (
              <PermissionDenied />
            )
          }
        />
        <Route
          path="/knowledge"
          element={
            checkPagePermission('knowledge') ? (
              <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <KnowledgeManage />
              </div>
            ) : (
              <PermissionDenied />
            )
          }
        />
        <Route
          path="/agents"
          element={
            checkPagePermission('agent') ? (
              <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <AgentList />
              </div>
            ) : (
              <PermissionDenied />
            )
          }
        />
        <Route
          path="/chat"
          element={
            checkPagePermission('chat') ? (
              <ChatPanel namespace="portal_chat" />
            ) : (
              <PermissionDenied />
            )
          }
        />
        <Route
          path="/settings/token"
          element={
            checkPagePermission('setting') ? (
              <TokenStats />
            ) : (
              <PermissionDenied />
            )
          }
        />
        <Route
          path="/settings/api"
          element={
            checkPagePermission('setting') ? (
              <ApiManage />
            ) : (
              <PermissionDenied />
            )
          }
        />
        <Route
          path="/settings/announcements"
          element={
            checkPagePermission('setting') ? (
              <AnnouncementManage />
            ) : (
              <PermissionDenied />
            )
          }
        />
        <Route
          path="/profile"
          element={
            checkPagePermission('setting') ? (
              <UserProfile />
            ) : (
              <PermissionDenied />
            )
          }
        />
        {/* 用户管理：会员列表、用户组管理，使用独立路径避免与“/ 跳转排课”冲突 */}
        <Route
          path="/users/groups"
          element={
            checkPagePermission('user_group') ? (
              <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <GroupList />
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', flexDirection: 'column', gap: 16 }}>
                <div style={{ fontSize: 48 }}>🚫</div>
                <div style={{ fontSize: 24, color: '#666' }}>权限不足</div>
              </div>
            )
          }
        />
        <Route
          path="/users"
          element={
            checkPagePermission('user') ? (
              <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <MemberList />
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', flexDirection: 'column', gap: 16 }}>
                <div style={{ fontSize: 48 }}>🚫</div>
                <div style={{ fontSize: 24, color: '#666' }}>权限不足</div>
              </div>
            )
          }
        />
        {/* 空间成员管理路由 */}
        <Route
          path="/space/:groupId/members"
          element={
            checkPagePermission('document') ?
              <SpaceMemberManage /> :
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '400px',
                flexDirection: 'column',
                gap: 16
              }}>
                <div style={{ fontSize: 48 }}>🚫</div>
                <div style={{ fontSize: 24, color: '#666' }}>权限不足</div>
                <div style={{ color: '#999' }}>您没有访问此页面的权限</div>
              </div>
          }
        />

        <Route path="/home" element={
          <div style={{ flex: 1, minHeight: 0, minWidth: 0, width: '100%', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
            <DashboardHome />
          </div>
        } />

        <Route path="*" element={<Navigate to="/home" replace />} />
      </Routes>
    );
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#fffbe9' }}>
      {/* 顶部栏 */}
      <div style={{ width: '100%', background: '#231a17', position: 'sticky', top: 0, zIndex: 1100, boxSizing: 'border-box', flexShrink: 0 }}>
        <div style={{ height: HEADER_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 32px', boxSizing: 'border-box' }}>
          {/* 左侧logo+品牌 */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <img src="/assets/logo.png" alt="logo" style={{ width: 38, height: 38, borderRadius: '50%', background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.10)', marginRight: 24, display: 'block' }} />
            <img src="/assets/logo-text.png" alt="blooart CAMPUS" style={{ height: 38, display: 'block' }} />
          </div>
          {/* 右侧欢迎语和按钮 */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span
              style={{ color: '#fff', fontSize: 14, marginRight: 16, textDecoration: 'underline', cursor: 'pointer', color: theme.PRIMARY_COLOR, fontWeight: 600 }}
              onClick={() => setShowProfile(true)}
            >
              欢迎您：{userInfo.real_name || userInfo.username}
            </span>
            <button type="button" onClick={handleLogout} style={{ background: theme.BUTTON_BG, color: '#231a17', border: 'none', borderRadius: 8, padding: '6px 20px', fontSize: 15, fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', transition: 'background 0.2s', position: 'relative', zIndex: 1101 }}>退出</button>
          </div>
        </div>
      </div>
      {/* 主体区域：侧边栏+内容区 */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
        {/* 侧边栏 */}
        <div style={{ width: 180, background: theme.PRIMARY_COLOR, minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`, boxShadow: '2px 0 8px #e5e5e5', borderRadius: 0, padding: '32px 0', display: 'flex', flexDirection: 'column', gap: 0, color: '#231a17', fontFamily: 'inherit' }}>
          {filteredMenuList.map(item => {
            const isOpen = openMenus.includes(item.key);
            return (
              <div key={item.key} onClick={() => handleMenuClick(item)}>
                <SidebarMenuItem
                  item={item}
                  isOpen={isOpen}
                  selectedKey={selectedKey}
                  setSelectedKey={setSelectedKey}
                  onChildClick={handleChildMenuClick}
                />
              </div>
            );
          })}
        </div>
        {/* 内容区 */}
        <div style={{ flex: 1, minWidth: 0, minHeight: 0, background: '#fffbe9', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {showProfile ? (
              <div style={{ position: 'relative', background: '#fff', minHeight: '100vh' }}>
                <button
                  onClick={() => setShowProfile(false)}
                  style={{
                    position: 'absolute',
                    left: 24,
                    top: 24,
                    zIndex: 10,
                    width: 36,
                    height: 36,
                    background: 'transparent',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    padding: 0
                  }}
                  title="返回"
                  aria-label="返回"
                >
                  <svg width="22" height="22" viewBox="0 0 22 22"><polyline points="14,5 8,11 14,17" fill="none" stroke="#d6e100" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </button>
                <div style={{ paddingTop: 48 }}><UserProfile /></div>
              </div>
            ) : (
              renderContent()
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
